import { useEffect, useState, useCallback } from 'react';
import { ContractFlowClient } from '../../api/clients/ContractFlowClient';
import FlowScreen from '../../v3/components/FlowScreen';
import SidebarBox from '../../v3/components/SidebarBox';
import Button from '../../v3/components/Button';
import {
  DEAL_VALUE_RANGE_MESSAGE,
  MAX_DEAL_VALUE,
  MIN_DEAL_VALUE,
  isDealValueInRange,
} from '../../v3/utils/dealValue';
import * as S from './OfferSimulator.styles';

const MONTHS = [24, 36, 48, 60, 72, 84, 96, 180, 200, 360];

const formatCurrency = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const formatManagementFee = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '-';
  return `${(Number(value) * 100).toFixed(2)}%`;
};

const formatApy = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '-';
  return `${Number(value)}%`;
};

const readQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    dealValue: params.get('dealValue') || '',
    term: params.get('months') || params.get('term') || '',
    seats: params.get('seats') || '1',
    referralCode: params.get('referralCode') || '',
  };
};

function OfferSimulator() {
  const initial = readQueryParams();
  const [dealValue, setDealValue] = useState(initial.dealValue);
  const [term, setTerm] = useState(initial.term || '96');
  const [seats, setSeats] = useState(initial.seats || '1');
  const [referralCode, setReferralCode] = useState(initial.referralCode);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(window.location.href);

  const runSimulation = useCallback(async (inputs) => {
    /* Every path into a simulation goes through here — the form and the shared link
       the form writes back — so the range is checked once, at the funnel. A deal
       above the ceiling gets a real quote from the API but no club to join, so
       refusing to ask is the point: an answer we cannot honour is worse than none. */
    if (!isDealValueInRange(Number(inputs.dealValue))) {
      setError(DEAL_VALUE_RANGE_MESSAGE);
      setData(null);
      return;
    }

    setLoading(true);
    setError(null);
    const [result, err] = await ContractFlowClient().getOfferSimulation({
      dealValue: inputs.dealValue,
      term: inputs.term,
      seats: inputs.seats,
      referralCode: inputs.referralCode || undefined,
    });
    setLoading(false);
    if (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.error?.message ||
        'Unable to simulate offer.';
      setError(message);
      setData(null);
      return;
    }
    setData(result);

    const query = new URLSearchParams({
      dealValue: String(inputs.dealValue),
      months: String(inputs.term),
      seats: String(inputs.seats),
    });
    if (inputs.referralCode) query.set('referralCode', inputs.referralCode);
    if (result?.clubId != null) query.set('clubId', String(result.clubId));
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}?${query.toString()}`
    );
    setShareUrl(`${window.location.origin}/?${query.toString()}`);
  }, []);

  useEffect(() => {
    if (initial.dealValue && initial.term) {
      runSimulation({
        dealValue: initial.dealValue,
        term: initial.term,
        seats: initial.seats || '1',
        referralCode: initial.referralCode,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!dealValue || !term || !seats) return;
    runSimulation({ dealValue, term, seats, referralCode });
  };

  const handleJoinNow = () => {
    window.location.assign(shareUrl);
  };

  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl);
      return;
    }
    const textarea = document.createElement('textarea');
    textarea.value = shareUrl;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  };

  return (
    <FlowScreen>
      <S.Wrapper>
          <S.TitleSection>
            <S.Title>Offer Simulator</S.Title>
            <S.Description>
              Explore monthly and weekly cycles for any deal value and share the
              result with a single link.
            </S.Description>
          </S.TitleSection>

          <SidebarBox variant="secondary" as="section">
            <S.Form onSubmit={handleSubmit}>
              <S.Field>
                <label>Vehicle Price</label>
                <input
                  type="number"
                  min={MIN_DEAL_VALUE}
                  max={MAX_DEAL_VALUE}
                  step="100"
                  value={dealValue}
                  onChange={(e) => setDealValue(e.target.value)}
                  required
                />
              </S.Field>

              <S.Field>
                <label>Months</label>
                <select
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                >
                  {MONTHS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </S.Field>

              <S.Field>
                <label>Contracts</label>
                <input
                  type="number"
                  min="1"
                  value={seats}
                  onChange={(e) => setSeats(e.target.value)}
                  required
                />
              </S.Field>

              <S.Field>
                <label>Referral code (optional)</label>
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value)}
                />
              </S.Field>

              <Button
                variant="brandPrimary"
                type="submit"
                disabled={loading}
              >
                {loading ? 'Simulating…' : 'Simulate'}
              </Button>
            </S.Form>

            {error && <S.ErrorBox>{error}</S.ErrorBox>}

            {data && (
              <>
                <S.SharedRows>
                  <S.Row>
                    <span>Fee</span>
                    <strong>{formatManagementFee(data.managementFee)}</strong>
                  </S.Row>
                  <S.Row>
                    <span>EAPR</span>
                    <strong>{formatApy(data.apy)}</strong>
                  </S.Row>
                  <S.Row>
                    <span>Total Financing</span>
                    <strong>{formatCurrency(data.groupValueWithFee)}</strong>
                  </S.Row>
                  {data.referralApplied && <S.Badge>Referral applied</S.Badge>}
                  {data.referralNotFound && (
                    <S.WarningBox>
                      Referral code not found. Showing default fee.
                    </S.WarningBox>
                  )}
                </S.SharedRows>

                <S.Cards>
                  <S.Card>
                    <S.CardTitle>Monthly cycle</S.CardTitle>
                    <S.BigAmount>
                      {formatCurrency(data.monthly.monthlyPayment)}
                      <S.Small>/month</S.Small>
                    </S.BigAmount>
                    {data.halfPaymentApplied && (
                      <S.Row>
                        <span>Half payment (referral)</span>
                        <strong>
                          {formatCurrency(data.monthly.halfPayment)}
                        </strong>
                      </S.Row>
                    )}
                    <S.Row>
                      <span>Term</span>
                      <strong>{term} months</strong>
                    </S.Row>
                  </S.Card>

                  <S.Card>
                    <S.CardTitle>Weekly cycle</S.CardTitle>
                    <S.BigAmount>
                      {formatCurrency(data.weekly.weeklyPayment)}
                      <S.Small>/week</S.Small>
                    </S.BigAmount>
                    {data.weekly.firstWeeklyPayment != null && (
                      <S.Row>
                        <span>First weekly payment</span>
                        <strong>
                          {formatCurrency(data.weekly.firstWeeklyPayment)}
                        </strong>
                      </S.Row>
                    )}
                    {data.halfPaymentApplied && (
                      <S.Row>
                        <span>Half payment (referral)</span>
                        <strong>
                          {formatCurrency(data.weekly.halfPayment)}
                        </strong>
                      </S.Row>
                    )}
                    <S.Row>
                      <span>Week term</span>
                      <strong>{data.weekly.weekTerm} weeks</strong>
                    </S.Row>
                  </S.Card>
                </S.Cards>

                <S.ShareRow>
                  <input type="text" readOnly value={shareUrl} />
                  <Button variant="invert" type="button" onClick={handleCopy}>
                    Copy
                  </Button>
                  <Button
                    variant="brandPrimary"
                    type="button"
                    onClick={handleJoinNow}
                  >
                    Join Now
                  </Button>
                </S.ShareRow>
              </>
            )}
        </SidebarBox>
      </S.Wrapper>
    </FlowScreen>
  );
}

export default OfferSimulator;
